/**
 * Calendar dates for sales, which are always the team's local dates.
 *
 * An order placed at 11pm in New York belongs to that day, not to the UTC day
 * after it. The local date and hour are fixed once, at ingest, so no query and
 * no chart ever does timezone arithmetic and a DST change can never move an
 * order from one day to another.
 *
 * Dates travel as 'YYYY-MM-DD' strings. A Date only appears where an instant is
 * meant.
 */
export const SALES_TIMEZONE = 'America/New_York';
const partsFormatter = new Map();
function formatterFor(timeZone) {
    let formatter = partsFormatter.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        });
        partsFormatter.set(timeZone, formatter);
    }
    return formatter;
}
function localParts(instant, timeZone) {
    const parts = {};
    for (const part of formatterFor(timeZone).formatToParts(instant))
        parts[part.type] = part.value;
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
        second: Number(parts.second),
    };
}
const pad = (n, width = 2) => String(n).padStart(width, '0');
/** The local calendar date of an instant, as 'YYYY-MM-DD'. */
export function toLocalDate(instant, timeZone = SALES_TIMEZONE) {
    const p = localParts(instant, timeZone);
    return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}
/** The local hour of an instant, 0–23. */
export function toLocalHour(instant, timeZone = SALES_TIMEZONE) {
    return localParts(instant, timeZone).hour;
}
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isIsoDate(value) {
    const match = DATE_PATTERN.exec(value);
    if (!match)
        return false;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.toISOString().slice(0, 10) === value;
}
function parseIsoDate(value) {
    const match = DATE_PATTERN.exec(value);
    if (!match || !isIsoDate(value))
        throw new Error(`Not a calendar date: ${value}`);
    return [Number(match[1]), Number(match[2]), Number(match[3])];
}
/** 'YYYY-MM-DD' shifted by whole days. */
export function addDays(date, days) {
    const [y, m, d] = parseIsoDate(date);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/**
 * The instant a local calendar day begins. Midnight exists on every day in
 * America/New_York (the DST jumps happen at 2am), so this is unambiguous.
 */
export function localDayStart(date, timeZone = SALES_TIMEZONE) {
    const [y, m, d] = parseIsoDate(date);
    // Guess the instant as if local were UTC, then correct by the zone's offset
    // at that guess. A second pass settles the rare case where the correction
    // itself crosses an offset change.
    let guess = Date.UTC(y, m - 1, d);
    for (let i = 0; i < 2; i++) {
        const p = localParts(new Date(guess), timeZone);
        const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
        guess -= asIfUtc - Date.UTC(y, m - 1, d);
    }
    return new Date(guess);
}
/**
 * The value to hand Prisma for a `@db.Date` column: UTC midnight of that date,
 * which Postgres stores as exactly that calendar date.
 */
export function dateForDb(date) {
    const [y, m, d] = parseIsoDate(date);
    return new Date(Date.UTC(y, m - 1, d));
}
/** The inverse of dateForDb, for a value read back from a `@db.Date` column. */
export function dateFromDb(value) {
    return value.toISOString().slice(0, 10);
}
//# sourceMappingURL=sales-dates.js.map